import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@material-ui/core';
import { Typography, Button, Loader } from 'kangarootime-ui';
import RaisedContainer from '../../../../../components/RaisedContainer';
import Header from './Header';
import { Mutation } from 'react-apollo';
import { GET_CHANNELS } from '../../../graphql/queries';
import { CREATE_NEW_CHANNEL } from '../../../graphql/mutations';

/**
 * wrapper component for shared aspects of a new chanel/direct message modal
 */
const CreateChannelModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  buttonText,
  buttonDisabled,
  newChannelDetails,
  showCreateChannelButton,
  existingChannels,
  employeeLegacyChatId,
  selectChannel,
  ...props
}) => {
  if (!isOpen) {
    return null;
  }

  // update the cache so it shows the newly created channels
  const handleMutationUpdate = (cache, { data: { createChannel } }) => {
    // read current channels data from the cache
    let { getChannels } = cache.readQuery({ query: GET_CHANNELS });
    const cachedChannelIds = getChannels.map(channel => channel.id);

    /**
     * there is a bug with react-apollo and appsync where `update` for mutations causes the duplicates to be added into the cache
     * - once with the actual server response and once with the optimistic ui
     * - if the new channel id has already been added, simply don't add it again
     */
    if (!cachedChannelIds.includes(createChannel.id)) {
      // add new channel
      getChannels.push(createChannel);
      // write updated data back to the cache
      cache.writeQuery({
        query: GET_CHANNELS,
        data: {
          getChannels
        }
      });
    }
  };

  /**
   * Dispatch graphql create channel mutation
   * @param {Function} mutationGqlFunction
   */
  const handleMutation = mutationGqlFunction => {
    let channelAlreadyExists = null;
    const { invitedUserIds } = newChannelDetails;
    let invitedUserIdsCopy = [...invitedUserIds].map(u => `${u.type}-${u.id}`);
    invitedUserIdsCopy.push(employeeLegacyChatId);
    invitedUserIdsCopy.sort();

    existingChannels.forEach(channel => {
      const { memberIds, directMessage } = channel;

      // only care if the existing channel is a direct message
      if (directMessage) {
        memberIds.sort();

        if (memberIds.every((id, index) => id === invitedUserIdsCopy[index])) {
          channelAlreadyExists = channel;
        }
      }
    });

    // channel with the selected members already exists. select it instead of creating it again
    if (channelAlreadyExists) {
      selectChannel(channelAlreadyExists.id, channelAlreadyExists);
      onClose();
    } else {
      mutationGqlFunction({
        variables: {
          details: newChannelDetails
        }
      });
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RaisedContainer className='flex-columns' style={{ height: '100%' }}>
        <Header closeModal={onClose} />
        <div className='flex' style={{ justifyContent: 'center', padding: '15vh 2rem 2rem' }}>
          <div className='flex-columns' style={{ width: 640, maxWidth: '100%' }}>
            <Typography type='headline'>{title}</Typography>
            <Typography>{subtitle}</Typography>
            { props.children }
            { showCreateChannelButton &&
              <Mutation
                mutation={CREATE_NEW_CHANNEL}
                onCompleted={onClose}
                update={handleMutationUpdate}
              >
                {(createChannel, { loading }) => (
                  <Loader loading={loading}>
                    <Button
                      disabled={buttonDisabled}
                      onClick={() => handleMutation(createChannel)}
                      style={{ marginTop: 8, alignSelf: 'flex-end' }}
                    >
                      { buttonText }
                    </Button>
                  </Loader>
                )}
              </Mutation>
            }
          </div>
        </div>
      </RaisedContainer>
    </Modal>
  );
};

CreateChannelModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string,
  buttonDisabled: PropTypes.bool,
  newChannelDetails: PropTypes.object, // details of a new channel (public, direct message, boradcast, etc.)
  showCreateChannelButton: PropTypes.bool,
  existingChannels: PropTypes.array,
  employeeLegacyChatId: PropTypes.string,
  selectChannel: PropTypes.func
};

CreateChannelModal.defaultProps = {
  showCreateChannelButton: true,
  existingChannels: [], // we don't pass this prop when creating a channel, only when creating a direct message
  selectChannel: () => {} // only used with the direct message modal to select a channel that already exists with the select members
};

export default CreateChannelModal;
